// §9.3 shell→app on the `window` channel — the shell telling an app about the desktop it lives in.
//
// A built-in reads these things by asking the kernel. An app in an iframe cannot: it has no access
// to the shell's state, and the browser tells it nothing about a window it does not own. So every
// one of §9.3's shell→app `window` messages is a push, and this is where they come from.
//
// §9.7 is the one that matters most. `skinChanged` carries the **re-sent reserved set** — the
// chords the shell claims change with the skin, and an app that kept honouring the old set would
// both swallow keys it should pass on and act on keys the shell has just claimed.
//
// The `closeRequested` half of the row is not here: it is a *question*, asked by `kernel.
// requestClose` and awaited (§18 F9), where all of these are statements.
import type { Rect, SkinId, Win } from '@hangar/shared';
import type { Bus } from '../bus';
import type { BridgeHost } from './host';

export interface BridgeEnvOptions {
  bridge: BridgeHost;
  bus: Bus;
  /** The window behind an id, for turning a bus event into the connection it concerns. */
  winOf: (winId: string) => Win | undefined;
  activeSkin: () => SkinId;
}

/**
 * Subscribe the bridge to the kernel's environment. Returns the detach.
 *
 * Every send goes only to connections that completed the handshake: an unbridged frame is a web
 * page that never asked (F7), and posting at it would be talking to something that is not
 * listening.
 */
export function attachBridgeEnv(options: BridgeEnvOptions): () => void {
  const { bridge, bus } = options;
  /** The last size each instance was told about, so a pure move is not reported as a resize. */
  const sizes = new Map<string, string>();
  let focused: string | null = null;

  function broadcast(key: 'window/skinChanged' | 'window/themeChanged', payload: unknown): void {
    for (const connection of bridge.connections()) {
      if (connection.state === 'bridged') connection.send(key, payload);
    }
  }

  function sendTo(winId: string | null, key: 'window/focus' | 'window/blur' | 'window/resized', payload: unknown): void {
    if (winId === null) return;
    const win = options.winOf(winId);
    if (!win) return;
    const connection = bridge.get(win.instanceKey);
    if (connection && connection.state === 'bridged') connection.send(key, payload);
  }

  const off = [
    // §9.7 step 4. Driven by the **keymap**, not by `settings:skinChanged`: that event fires while
    // the old keymap is still loaded — the new one arrives from a React effect a tick later — so
    // an app would be handed the outgoing skin's set. It also covers a §11.6 override, which
    // changes what the shell claims without any skin changing at all.
    bus.on('keys:reservedChanged', ({ reservedChords }) => {
      broadcast('window/skinChanged', { skin: options.activeSkin(), reservedChords });
    }),

    // Already collapsed from the `system` preference by whoever emitted it, so an app is told
    // what to draw rather than a preference it would have to resolve without a desktop to ask.
    bus.on('settings:themeChanged', ({ theme }) => {
      broadcast('window/themeChanged', { theme });
    }),

    bus.on('window:focused', ({ id }) => {
      if (id === focused) return;
      const previous = focused;
      focused = id;
      // Blur first: an app that repaints on focus should not do it while another still believes
      // it has the keyboard.
      sendTo(previous, 'window/blur', {});
      sendTo(id, 'window/focus', {});
    }),

    bus.on('window:moved', ({ id, rect }) => {
      const win = options.winOf(id);
      if (!win) return;
      // `window:moved` is every committed geometry change, which includes moves that changed no
      // size at all. §9.3 says `resized`, so that is what this reports.
      const size = sizeKey(rect);
      if (sizes.get(win.instanceKey) === size) return;
      sizes.set(win.instanceKey, size);
      sendTo(id, 'window/resized', { w: rect.w, h: rect.h });
    }),

    bus.on('window:closed', ({ instanceKey }) => {
      sizes.delete(instanceKey);
    }),
  ];

  return () => {
    for (const unsubscribe of off) unsubscribe();
    sizes.clear();
  };
}

function sizeKey(rect: Rect): string {
  return `${rect.w}x${rect.h}`;
}
